/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   backupReadiness.ts
 *     Description     :   backupReadiness.ts — typed client for the Spec 236 readiness read surface.
 *                         All GETs hit the backup-health router; the backend reads dbo only (never probes Azure).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-10
 *     Modified        :   2026-10-30 | Peter Dobler | Spec 236 T053: standard header (Principle XXXVII)
 * =========================================================================================
 */

import apiClient from '../services/api';

type Wrapped<T> = { success: boolean; data: T };

const BASE = '/api/v1/backup/health';

export type ReadinessStatus = 'ready' | 'empty_expected' | 'at_risk' | 'blocked' | 'unknown';

export interface ReadinessProblem {
  entityType: string;
  entityId: string;
  entityName: string | null;
  status: ReadinessStatus | null;
  severityRank: number;
  primaryReasonCode: string | null;
  reasonCodes: string[];
  reasonCategory: string | null;
  title: string | null;
  remediation: string | null;
  selfGrantable: boolean | null;
  blockerCount: number;
  riskCount: number;
  isEmptyExpected: boolean;
  baselineStale: boolean;
  collectedTableCount: number | null;
  discoverableTableCount: number | null;
  checkSource: string | null;
  lastEvaluatedAt: string | null;
}

export interface ReadinessListResponse {
  rows: ReadinessProblem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReadinessSummary {
  blocked: number;
  atRisk: number;
  unknown: number;
  baselineStale: number;
  readyOrEmpty: number;
  evaluatedEntities: number;
  enrolledEntities: number;
  willFail: number;
  lastPassAt: string | null;
}

export interface ReadinessCheck {
  checkCode: string;
  result: string;
  severity: string;
  tier: string;
  observed: string | null;
  expected: string | null;
  detail: string | null;
  title: string | null;
  remediation: string | null;
  selfGrantable: boolean | null;
}

export interface ReadinessDetail {
  entityType: string;
  entityId: string;
  entityName: string | null;
  status: ReadinessStatus;
  severityRank: number;
  primaryReasonCode: string | null;
  reasonCodes: string[];
  blockerCount: number;
  riskCount: number;
  isEmptyExpected: boolean;
  baselineStale: boolean;
  collectedTableCount: number | null;
  discoverableTableCount: number | null;
  checkSource: string;
  lastEvaluatedAt: string | null;
  checks: ReadinessCheck[];
}

export interface ReadinessListParams {
  status?: string;
  entityType?: string;
  page?: number;
  pageSize?: number;
}

export const backupReadinessApi = {
  /** A1 — predicted-problems list (blocked/at_risk not already surfaced as an occurred failure). */
  list: async (params: ReadinessListParams = {}): Promise<ReadinessListResponse> =>
    (await apiClient.get<Wrapped<ReadinessListResponse>>(
      `${BASE}/readiness`,
      params as Record<string, string | number | boolean | undefined>,
    )).data,

  /** A2 — fleet summary (StatCard row + tab badge). */
  summary: async (): Promise<ReadinessSummary> =>
    (await apiClient.get<Wrapped<ReadinessSummary>>(`${BASE}/readiness/summary`)).data,

  /** A3 — per-entity verdict + per-check breakdown (the drawer). */
  entity: async (entityType: string, entityId: string): Promise<ReadinessDetail> =>
    (await apiClient.get<Wrapped<ReadinessDetail>>(`${BASE}/readiness/${entityType}/${entityId}`)).data,
};

export default backupReadinessApi;
